import { useMemo } from 'react';

import { buildVersionOptions } from '@/components/deploy/version-sort';
import { useConfigVersions } from '@/hooks/use-config-versions';

// --- Props ---

interface VersionSelectorProps {
  projectKey: string;
  selectedVersion: string;
  onVersionChange: (version: string) => void;
}

// --- Component ---

export function VersionSelector({
  projectKey,
  selectedVersion,
  onVersionChange,
}: VersionSelectorProps) {
  const { data: versions, isLoading, isError, error } = useConfigVersions(projectKey);

  const options = useMemo(
    () => buildVersionOptions(versions ?? []),
    [versions]
  );

  if (isLoading) {
    return (
      <div>
        <label className="block text-xs font-medium text-content-secondary mb-1">
          Version
        </label>
        <div className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content-tertiary animate-pulse">
          Loading versions…
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="version-selector" className="block text-xs font-medium text-content-secondary mb-1">
        Version
      </label>
      <select
        id="version-selector"
        value={selectedVersion}
        onChange={(e) => onVersionChange(e.target.value)}
        className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
      >
        {options.map((version) => (
          <option key={version} value={version}>
            {version}
          </option>
        ))}
      </select>
      {isError && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error instanceof Error ? error.message : 'Failed to load versions'}
        </p>
      )}
    </div>
  );
}
